import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/layout/Layout";
import RoleGuard from "../components/common/RoleGuard";
import Login from "../components/auth/Login";
import Register from "../components/auth/Register";
import ForgotPassword from "../components/auth/ForgotPassword";
import DashboardPage from "../pages/DashboardPage";
import ProjectsPage from "../pages/ProjectsPage";
import ProjectDetailPage from "../pages/ProjectDetailPage";
import StagesPage from "../pages/StagesPage";
import TasksPage from "../pages/TasksPage";
import InventoryPage from "../pages/InventoryPage";
import LaborPage from "../pages/LaborPage";
import ReportsPage from "../pages/ReportsPage";
import SettingsPage from "../pages/SettingsPage";
import ProfilePage from "../pages/ProfilePage";
import LoadingSpinner from "../components/common/LoadingSpinner";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Projects - Accessible by Super Admin, Project Manager, Sub Manager */}
        <Route
          path="projects"
          element={
            <RoleGuard
              allowedRoles={["super_admin", "project_manager", "sub_manager"]}
            >
              <ProjectsPage />
            </RoleGuard>
          }
        />
        <Route
          path="projects/:id"
          element={
            <RoleGuard
              allowedRoles={["super_admin", "project_manager", "sub_manager"]}
            >
              <ProjectDetailPage />
            </RoleGuard>
          }
        />
        <Route
          path="projects/:projectId/stages"
          element={
            <RoleGuard
              allowedRoles={["super_admin", "project_manager", "sub_manager"]}
            >
              <StagesPage />
            </RoleGuard>
          }
        />

        {/* Stages - Accessible by Super Admin, Project Manager, Sub Manager */}
        <Route
          path="stages"
          element={
            <RoleGuard
              allowedRoles={["super_admin", "project_manager", "sub_manager"]}
            >
              <StagesPage />
            </RoleGuard>
          }
        />

        {/* Tasks - Accessible by all logged-in users */}
        <Route path="tasks" element={<TasksPage />} />

        {/* Inventory - Accessible by Super Admin, Project Manager, Sub Manager */}
        <Route
          path="inventory"
          element={
            <RoleGuard
              allowedRoles={["super_admin", "project_manager", "sub_manager"]}
            >
              <InventoryPage />
            </RoleGuard>
          }
        />

        {/* Labor - Accessible by Super Admin, Project Manager, Sub Manager, Foreman */}
        <Route
          path="labor"
          element={
            <RoleGuard
              allowedRoles={[
                "super_admin",
                "project_manager",
                "sub_manager",
                "foreman",
              ]}
            >
              <LaborPage />
            </RoleGuard>
          }
        />

        {/* Reports - Accessible by Super Admin, Project Manager */}
        <Route
          path="reports"
          element={
            <RoleGuard allowedRoles={["super_admin", "project_manager"]}>
              <ReportsPage />
            </RoleGuard>
          }
        />

        {/* Settings - Accessible by Super Admin, Project Manager */}
        <Route
          path="settings"
          element={
            <RoleGuard allowedRoles={["super_admin", "project_manager"]}>
              <SettingsPage />
            </RoleGuard>
          }
        />

        {/* Profile - Accessible by all logged-in users */}
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

// Simple 404 Component
const NotFoundPage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="text-center">
        <div className="text-6xl mb-4">🔧</div>
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white">
          404
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">
          Page not found
        </p>
        <a href="/dashboard" className="btn-primary inline-block mt-6">
          Go to Dashboard
        </a>
      </div>
    </div>
  );
};

export default AppRoutes;
